import { io, Socket } from 'socket.io-client'
import type { AppDispatch } from '../redux/store'
import {
  socketConnected,
  socketDisconnected,
  socketError,
  receiveMessage,
  socketReconnecting,
} from '../redux/reducers/socketSlice'
import { updateMetrics, addAlert } from '../redux/reducers/monitoringSlice'
import { CONFIG } from '../../config'

class WebSocketService {
  private socket: Socket | null = null
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private reconnectAttempts = 0
  private maxReconnectAttempts = 5
  private reconnectDelay = 3000
  private dispatch: AppDispatch | null = null

  setDispatch(dispatch: AppDispatch) {
    this.dispatch = dispatch
  }

  connect() {
    if (this.socket?.connected) {
      console.log('WebSocket already connected')
      return
    }

    const token = localStorage.getItem('admin_access_token')

    if (!token) {
      console.error('Missing authentication token for WebSocket')
      if (this.dispatch) {
        this.dispatch(socketError('Missing authentication token'))
      }
      return
    }

    this.socket = io(CONFIG.service.socketUrl, {
      query: {
        accessToken: token,
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: this.reconnectDelay,
    })

    this.setupEventListeners()
  }

  private setupEventListeners() {
    if (!this.socket) return

    // Connection events
    this.socket.on('connect', () => {
      console.log('WebSocket connected')
      if (this.dispatch) {
        this.dispatch(socketConnected())
      }
      this.reconnectAttempts = 0
      
      // Subscribe to provider channels
      this.subscribeToProviderChannels()
    })

    this.socket.on('disconnect', (reason) => {
      console.log('WebSocket disconnected:', reason)
      if (this.dispatch) {
        this.dispatch(socketDisconnected())
      }
      
      if (reason === 'io server disconnect') {
        // Server initiated disconnect, attempt reconnect
        this.attemptReconnect()
      }
    })

    this.socket.on('connect_error', (error) => {
      console.error('WebSocket connection error:', error)
      if (this.dispatch) {
        this.dispatch(socketError(error.message))
      }
    })

    this.socket.on('reconnect_attempt', (attemptNumber) => {
      console.log(`WebSocket reconnection attempt ${attemptNumber}`)
      if (this.dispatch) {
        this.dispatch(socketReconnecting())
      }
    })

    // Provider-specific events
    this.socket.on('system:metrics', (data) => {
      if (this.dispatch) {
        this.dispatch(updateMetrics(data))
        this.dispatch(receiveMessage({ event: 'system:metrics', data }))
      }
    })

    this.socket.on('system:alert', (data) => {
      if (this.dispatch) {
        this.dispatch(addAlert(data))
        this.dispatch(receiveMessage({ event: 'system:alert', data }))
      }
    })

    this.socket.on('client:activity', (data) => {
      if (this.dispatch) {
        this.dispatch(receiveMessage({ event: 'client:activity', data }))
      }
    })

    this.socket.on('client:statusChange', (data) => {
      if (this.dispatch) {
        this.dispatch(receiveMessage({ event: 'client:statusChange', data }))
      }
    })

    // Error events
    this.socket.on('error', (error) => {
      console.error('WebSocket error:', error)
      if (this.dispatch) {
        this.dispatch(socketError(error.message || 'WebSocket error'))
      }
    })

    this.socket.on('unauthorized', (error) => {
      console.error('WebSocket unauthorized:', error)
      if (this.dispatch) {
        this.dispatch(socketError('Unauthorized'))
      }
      this.disconnect()
    })
  }

  private subscribeToProviderChannels() {
    if (!this.socket) return

    // Subscribe to provider-specific channels
    this.emit('subscribe', {
      channels: [
        'system:metrics',
        'system:alerts',
        'client:all', // Monitor all client activity
        'operations:status',
      ],
    })
  }

  private attemptReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached')
      if (this.dispatch) {
        this.dispatch(socketError('Unable to reconnect to server'))
      }
      return
    }

    this.reconnectAttempts++
    
    this.reconnectTimer = setTimeout(() => {
      console.log(`Attempting reconnection ${this.reconnectAttempts}/${this.maxReconnectAttempts}`)
      this.connect()
    }, this.reconnectDelay * this.reconnectAttempts)
  }

  disconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }

    if (this.socket) {
      this.socket.removeAllListeners()
      this.socket.disconnect()
      this.socket = null
    }

    if (this.dispatch) {
      this.dispatch(socketDisconnected())
    }
  }

  emit(event: string, data?: any) {
    if (!this.socket?.connected) {
      console.error('Cannot emit event: WebSocket not connected')
      return
    }

    this.socket.emit(event, data)
  }

  // Provider-specific methods
  subscribeToClient(clientId: string) {
    this.emit('subscribe:client', { clientId })
  }

  unsubscribeFromClient(clientId: string) {
    this.emit('unsubscribe:client', { clientId })
  }

  requestMetrics() {
    this.emit('request:metrics')
  }

  requestAlerts(filters?: any) {
    this.emit('request:alerts', filters)
  }

  broadcastToClients(event: string, data: any, clientIds?: string[]) {
    this.emit('broadcast:clients', { event, data, clientIds })
  }

  isConnected(): boolean {
    return this.socket?.connected || false
  }
}

// Create singleton instance
const webSocketService = new WebSocketService()

export default webSocketService